import React, { useRef, useState } from 'react';
import { Modal, Collapse, Carousel } from 'antd';
import { fakeProImage } from '../../config/fakeData';
import { ModalCarouselWrapper } from "./style";
import { AddCommas, primaryColor } from '../../config';
import { Link } from 'react-router-dom';
import { useThemeMode } from '../../contexts/ThemeContext';
import { formatAmazonFeeLabel, getAmazonFeeBreakdownEntries } from '../../utils/amazonFeesBreakdown';

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const SLIDE_LABELS = ['Performance', 'Cost', 'Profit'];

const getNestedValue = (row, path) => {
    if (!row) return undefined;
    if (Array.isArray(path)) {
        return path.reduce((acc, key) => (acc == null ? undefined : acc[key]), row);
    }
    return row[path];
};

const calculateChangePercent = (currentValue, previousValue) => {
    const current = Number(currentValue);
    const previous = Number(previousValue);
    if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
    if (current === 0) {
        if (previous === 0) return 0;
        return null;
    }
    return ((current - previous) / Math.abs(current)) * 100;
};

const formatComparePct = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
};

const OrderDetailsModal = ({ open, onClose, data, currencySign }) => {
    const mainCollapseRef = useRef(null);
    const carouselRef     = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const { themeMode } = useThemeMode();
    const isDark = themeMode === 'dark';

    // const getAmount = (val) => {
    //     const amount = parseFloat(val || 0);
    //     return `${amount < 0 ? '-' : ''}${currencySign}${Math.abs(amount).toFixed(2)}`;
    // };
    const getAmount = (val) => {
        const amount = parseFloat(val || 0);
        const formattedNumber = AddCommas(Math.abs(amount).toFixed(2));
        return `${amount < 0 ? '-' : ''}${currencySign}${formattedNumber}`;
    };

    const renderCompareLine = (valuePath, formatter = (value) => value) => {
        const compareWith = data?.compare_with;
        const compareRow = compareWith?.row;
        if (!compareWith || !compareRow) return null;

        const currentValue = getNestedValue(data, valuePath);
        const previousValue = getNestedValue(compareRow, valuePath);
        const hasPreviousValue = previousValue !== undefined && previousValue !== null && previousValue !== '';
        const pctValue = calculateChangePercent(currentValue, previousValue);
        const pctText = formatComparePct(pctValue);
        if (!hasPreviousValue && !pctText) return null;

        const pctColor = Number.isFinite(pctValue)
            ? pctValue >= 0
                ? (isDark ? '#36d399' : '#1a7f37')
                : (isDark ? '#ff6b81' : '#cf1322')
            : 'var(--app-text-muted)';

        return (
            <span
                className="fs-9 fw-semibold mt-1"
                style={{ color: 'var(--app-text-muted)', lineHeight: 1.2, whiteSpace: 'nowrap' }}
                title={compareWith?.start_date && compareWith?.end_date ? `${compareWith.start_date} to ${compareWith.end_date}` : undefined}
            >
                vs {hasPreviousValue ? formatter(previousValue) : 'prior period'}
                {pctText ? <span className="ms-1" style={{ color: pctColor }}>{pctText}</span> : null}
            </span>
        );
    };

    const handleCollapseChange = (activeKeys) => {
        if (Array.isArray(activeKeys) && activeKeys.length > 0) {
            const newlyOpenedKey = activeKeys[activeKeys.length - 1];
            const panelHeader = mainCollapseRef.current?.querySelector(`.ant-collapse-item[data-key="${newlyOpenedKey}"] .ant-collapse-header`);
            if (panelHeader) panelHeader.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    };

    const generateOrderMetrics = (orderData) => {
        if (!orderData || !orderData.more_info) return { revenueMetrics: [], costMetrics: [], profitabilityMetrics: [] };

        const info = orderData.more_info;

        const revenueMetrics = [
            { key: '1', title: 'Sales', amount: getAmount(info.sales), compare: renderCompareLine(['more_info', 'sales'], getAmount), items: [] },
            { key: '2', title: 'Units', amount: `${info.units || 0}`, compare: renderCompareLine(['more_info', 'units'], (value) => `${value || 0}`), items: [] },
            { key: '3', title: 'Promo', amount: getAmount(info.promo), compare: renderCompareLine(['more_info', 'promo'], getAmount), items: [] },
            { key: '4', title: 'Gift Wrap', amount: getAmount(info.giftwrap), compare: renderCompareLine(['more_info', 'giftwrap'], getAmount), items: [] },
        ];

        const costMetrics = [
            {
                key: '1',
                title: 'Amazon Fees',
                amount: getAmount(info.amazon_fees?.total),
                compare: renderCompareLine(['more_info', 'amazon_fees', 'total'], getAmount),
                items: getAmazonFeeBreakdownEntries(info.amazon_fees).map(([key, value]) => ({
                    label: formatAmazonFeeLabel(key),
                    value: getAmount(value),
                    compare: renderCompareLine(['more_info', 'amazon_fees', key], getAmount),
                }))
            },
            { key: '2', title: 'Shipping Cost', amount: getAmount(-info.shipping_cost), compare: renderCompareLine(['more_info', 'shipping_cost'], (value) => getAmount(-value)), items: [] },
            { key: '3', title: 'Cost of Goods', amount: getAmount(-info.cost_of_goods), compare: renderCompareLine(['more_info', 'cost_of_goods'], (value) => getAmount(-value)), items: [] },
            { key: '4', title: 'Tax Collected', amount: getAmount(orderData.total_vat), compare: renderCompareLine(['total_vat'], getAmount), items: [] },
        ];

        const profitabilityMetrics = [
            { key: '1', title: 'Gross Profit', amount: getAmount(info.gross_profit), compare: renderCompareLine(['more_info', 'gross_profit'], getAmount), items: [] },
            { key: '2', title: 'Net Profit', amount: getAmount(info.net_profit), compare: renderCompareLine(['more_info', 'net_profit'], getAmount), items: [] },
            { key: '3', title: 'Estimated Payout', amount: getAmount(info.estimated_payout), compare: renderCompareLine(['more_info', 'estimated_payout'], getAmount), items: [] },
        ];

        const format = (items) => items.map(section => ({
            key: section.key,
            label: (
                <div className="d-flex justify-content-between w-100">
                    <span className="text-gray-800 fw-bold fs-6">{section.title}</span>
                    <span className="text-gray-800 fw-bolder fs-6 me-3 d-flex flex-column align-items-end">
                        <span>{section.amount}</span>
                        {section.compare}
                    </span>
                </div>
            ),
            children: section.items?.length > 0 ? (
                <div className="ps-4">
                    {section.items.map((item, i) => (
                        <div key={i} className="d-flex justify-content-between py-1 border-bottom border-bottom-dashed">
                            <span className="text-gray-600">{item.label}</span>
                            <span className="fw-bold d-flex flex-column align-items-end">
                                <span>{item.value}</span>
                                {item.compare}
                            </span>
                        </div>
                    ))}
                </div>
            ) : null,
            showArrow: section.items?.length > 0,
            collapsible: section.items?.length > 0 ? '' : 'disabled'
        }));

        return {
            revenue: format(revenueMetrics),
            costs: format(costMetrics),
            profitability: format(profitabilityMetrics)
        };
    };

    const { revenue, costs, profitability } = generateOrderMetrics(data);

    return (
        <ModalCarouselWrapper>
            <Modal open={open} onCancel={onClose} footer={null} width={650} centered className="custom-modal">
                {data && (
                    <div style={{ padding: '20px' }}>
                        <div className="text-center mb-4">
                            <img src={data.image_url || fakeProImage} alt="Product" style={{ maxHeight: 120, objectFit: 'contain' }} />
                            <h3 className="mt-3 fs-5 px-5">
                                <Link
                                    to={data.asin ? `${AMAZON_BASE_URL}${data.asin}` : "#"}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ lineHeight:1.6}}
                                    className='text-dark text-hover-danger'
                                >
                                    {data.product_name}
                                </Link>
                           
                            </h3>
                            <div className="text-muted fs-7">Order ID: <b className="text-dark">{data.order_id}</b></div>
                        </div>

                        {/* Tab navigation — synced with carousel */}
                        <div style={{ display: 'flex', gap: 6, width: '100%', paddingBottom: 2, marginBottom: 12 }}>
                            {SLIDE_LABELS.map((label, i) => {
                                const isActive = i === currentSlide;
                                return (
                                    <button
                                        key={label}
                                        onClick={() => carouselRef.current?.goTo(i)}
                                        aria-label={`Show ${label}`}
                                        title={label}
                                        style={{
                                            flex: 1,
                                            padding: '7px 8px',
                                            border: `1.5px solid ${isActive ? primaryColor : 'var(--app-border)'}`,
                                            borderRadius: 8,
                                            background: isActive ? primaryColor : 'transparent',
                                            color: isActive ? '#ffffff' : 'var(--app-text-muted)',
                                            fontSize: 12.5,
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            outline: 'none',
                                            whiteSpace: 'nowrap',
                                            textAlign: 'center',
                                            boxShadow: isActive ? `0 3px 10px ${primaryColor}59` : 'none',
                                            transition: 'background 0.2s ease, color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
                                        }}
                                    >
                                        {label}
                                    </button>
                                );
                            })}
                        </div>

                        <Carousel
                            ref={carouselRef}
                            dots={false}
                            afterChange={(index) => setCurrentSlide(index)}
                        >
                            <div>
                                <div className="modal-collapse-scrollable">
                                    <Collapse items={revenue} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef}/>
                                </div>
                            </div>
                            <div>
                                <div className="modal-collapse-scrollable">
                                    <Collapse items={costs} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef}/>
                                </div>
                            </div>
                            <div>
                                <div className="modal-collapse-scrollable">
                                    <Collapse items={profitability} bordered={false} onChange={handleCollapseChange} ref={mainCollapseRef}/>
                                </div>
                            </div>
                        </Carousel>

                        <div
                            className="mt-5 p-4 rounded border border-dashed d-flex justify-content-between align-items-center"
                            style={{
                                backgroundColor: isDark ? 'rgba(15, 126, 62, 0.18)' : '#DAF3E4',
                                borderColor: isDark ? 'rgba(54, 211, 153, 0.35)' : undefined,
                            }}>
                            <h4 className="mb-0 fw-bold" style={{ color: isDark ? '#36d399' : '#0F7E3E' }}>Net Profit</h4>
                            <h2 className="mb-0 fw-bolder d-flex flex-column align-items-end" style={{ color: isDark ? '#36d399' : '#0F7E3E' }}>
                                <span>{getAmount(data.more_info?.net_profit)}</span>
                                {renderCompareLine(['more_info', 'net_profit'], getAmount)}
                            </h2>
                        </div>
                    </div>
                )}
            </Modal>
        </ModalCarouselWrapper>
    );
};

export default OrderDetailsModal;

